import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import SntLabIcon from "@icons/SntLabIcon";
import { FeatureKey } from "@/types/generated/FeatureKey";
import { hasFeature } from "@/hooks/useFeatures";
import { setSideBarColors } from "../../../Theme/ThemeHelper";

import organizationClient from "@apis/organizationClient";

import MenuType from "./menuTypeConst";
import useAvailibleMenu from "./useAvailibleMenu";

const useAdminMenu = (loginInfo, orgId) => {
  const { custumInfo, organisation } = loginInfo;
  const language = useSelector((state) => state.language);
  const [orgSelected, setOrgSelected] = useState(null);

  const routes = useMemo(() => {
    const { features, permissionsMap } = loginInfo;

    return [
      {
        collapse: true,
        name: language.trackers_and_sensors_key,
        state: "openTrackersSensors",
        icon: (
          <span className="tracker-profile-admin-menu-icon menu-icon"></span>
        ),
        views: [
          {
            view: features["factory_upload"],
            to: "/factory_upload",
            layout: "",
            name: language.sidebar_factory_upload_key,
            subPages: ["/factory_upload_update", "/factory_order_create"],
          },
          {
            view: permissionsMap["MENU_ADMIN_TRACKERS"],
            to: "/trackers_admin",
            layout: "",
            name: language.UserPermission_MENU_ADMIN_TRACKERS_description_key,
            subPages: [
              "/trackers_admin_update",
              "/import_tracker_settings_admin",
            ],
          },
          {
            view: permissionsMap["MENU_ADMIN_BLE_SENSOR"],
            to: "/ble_sensor_list",
            layout: "",
            name: language.UserPermission_MENU_ADMIN_BLE_SENSOR_description_key,
            subPages: ["/ble_sensor_update", "/ble_sensor_import"],
          },
          {
            view:
              organisation.organisationType === "NORMAL" &&
              permissionsMap["MENU_ADMIN_USAGE_PROFILE"],
            to: "/device_usage_profile_admin",
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_USAGE_PROFILE_description_key,
            subPages: ["/device_usage_profile_manage_admin"],
          },
          {
            view: permissionsMap["MENU_ADMIN_EOL_TRACKERS"],
            to: "/end_of_life",
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_EOL_TRACKERS_description_key,
          },
          {
            view:
              features.bulk_operations &&
              permissionsMap["MENU_ADMIN_BULK_OPERATION"],
            to: "/bulk_manage_admin",
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_BULK_OPERATION_description_key,
            subPages: ["/bulk_results_detail", "/bulk_operations_admin"],
          },
          {
            view:
              (features["diagnostic_tab"] ||
                features["diagnostic_organisation"]) &&
              permissionsMap["MENU_ADMIN_DIAGNOSTIC"],
            to: "/diagnostic_organisation",
            layout: "",
            name: language.UserPermission_MENU_ADMIN_DIAGNOSTIC_description_key,
          },
        ],
      },
      {
        collapse: true,
        name: language.location_key,
        state: "openLocation",
        icon: <span className="location-stop-icon menu-icon"></span>,
        views: [
          {
            view:
              features["geozone_manager"] &&
              permissionsMap["MENU_ADMIN_GEOZONES"],
            to: "/areas",
            layout: "",
            name: language.UserPermission_MENU_ADMIN_GEOZONES_description_key,
            subPages: ["/areas_update", "/import_export_geozones"],
          },
          {
            view:
              features["metar_weather_location"] &&
              permissionsMap["MENU_ADMIN_METAR_WEATHER_LOCATION"],
            to: "/metar_weather_location",
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_METAR_WEATHER_LOCATION_description_key,
            subPages: ["/metar_weather_location_manage"],
          },
          {
            view:
              features["loc_geolayer"] && permissionsMap["MENU_ADMIN_GEOLAYER"],
            to: "/geolayers",
            layout: "",
            name: language.UserPermission_MENU_ADMIN_GEOLAYER_description_key,
            subPages: ["/geolayer_update"],
          },
          {
            view:
              features["loc_beacon"] &&
              permissionsMap["MENU_ADMIN_INFRASTRUCTURE"],
            to: "/infrastructures_admin",
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_INFRASTRUCTURE_description_key,
            subPages: ["/infrastructures_update"],
          },
          {
            view:
              features["private_wifi_resolution"] &&
              permissionsMap["MENU_ADMIN_WIFI"],
            to: `/wifi`,
            layout: "",
            name: language.UserPermission_MENU_ADMIN_WIFI_description_key,
          },
          {
            view:
              features["only_private_cellular_resolution"] &&
              permissionsMap["MENU_ADMIN_PRIVATE_CELLULAR"],
            to: `/private_cellular`,
            layout: "",
            name: language.UserPermissionCategory_PRIVATE_CELLULAR_title_key,
          },
          {
            view: permissionsMap["MENU_ADMIN_BLE_TAG_TRACKING_SETTINGS"],
            to: "/beacon_settings",
            param: permissionsMap["MANAGE_BLE_TAG_TRACKING_SETTINGS"]
              ? "type=edit"
              : "type=view",
            layout: "",
            name: language.beacon_settings_title,
          },
        ],
      },
      {
        collapse: true,
        name: language.automation_key,
        state: "openAutomationRule",
        icon: <span className="automation-category-icon menu-icon"></span>,
        views: [
          {
            view:
              features["automation_manager"] &&
              permissionsMap["MENU_ADMIN_AUTOMATION"],
            to: "/automation_rule",
            layout: "",
            name: language.rules_key,
            subPages: ["/automation_rule_create", "/automation_rule_update"],
          },
        ],
      },
      {
        collapse: true,
        name: language.tags_key,
        state: "openTags",
        icon: <span className="tag-icon menu-icon"></span>,
        views: [
          {
            view: permissionsMap["MENU_ADMIN_ASSET_TAGS"],
            to: "/asset_tags_manage",
            subPages: ["/update_device_tag"],
            layout: "",
            name: language.UserPermission_MENU_ADMIN_ASSET_TAGS_description_key,
          },
          {
            view:
              orgSelected &&
              "PARTNER" === orgSelected.organisationType &&
              permissionsMap["MENU_ADMIN_PARTNER_TAGS"],
            isForceAvailableMenu: true,
            to: "/partner_tag",
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_PARTNER_TAGS_description_key,
            subPages: ["/partner_tag_manage"],
          },
          {
            view:
              orgSelected &&
              "SYSTEM" === orgSelected.organisationType &&
              permissionsMap["MENU_ADMIN_SYSTEM_TAGS"],
            isForceAvailableMenu: true,
            to: "/system_tag",
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_SYSTEM_TAGS_description_key,
            subPages: ["/system_tag_manage"],
          },
          {
            view:
              features["geozone_manager"] &&
              permissionsMap["MENU_ADMIN_GEOZONE_TAGS"],
            to: "/geozone_tags_manage",
            subPages: ["/update_geozone_tag"],
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_GEOZONE_TAGS_description_key,
          },
        ],
      },
      {
        collapse: true,
        name: language.insights_key,
        state: "openInsights",
        icon: <span className="insights-icon menu-icon"></span>,
        views: [
          {
            view:
              orgSelected &&
              "SYSTEM" !== orgSelected.organisationType &&
              features["alert_ruleconf"] &&
              permissionsMap["MENU_ADMIN_ALERT_RULE"],
            isForceAvailableMenu: true,
            to: "/manage_alerts",
            layout: "",
            name: language.UserPermission_MENU_ADMIN_ALERT_RULE_description_key,
            subPages: ["/manage_alerts_update"],
          },
          {
            view:
              features["utilization_basic"] &&
              permissionsMap["MENU_ADMIN_DEVICE_UTILIZATION_SCHEME"],
            to: "/utilization_scheme",
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_DEVICE_UTILIZATION_SCHEME_description_key,
            subPages: ["/utilization_scheme_update"],
          },
          {
            view:
              features["flow_definition"] && permissionsMap["MENU_ADMIN_FLOW"],
            to: "/flow_definition",
            layout: "",
            name: language.UserPermission_MENU_ADMIN_FLOW_description_key,
            subPages: ["/flow_settings"],
          },
          {
            view:
              orgSelected &&
              "NORMAL" === orgSelected.organisationType &&
              features["process_manager"] &&
              permissionsMap["MENU_ADMIN_PROCESS_DEFINITION"],
            isForceAvailableMenu: true,
            to: "/process_definition",
            layout: "",
            title:
              language.UserPermission_MENU_ADMIN_PROCESS_DEFINITION_description_key,
            name: (
              <>
                {
                  language.UserPermission_MENU_ADMIN_PROCESS_DEFINITION_description_key
                }
                <SntLabIcon
                  size={14}
                  className={"ms-2"}
                  title={language.lab_feature_key}
                />
              </>
            ),
            subPages: ["/process_definition_update"],
          },
          {
            view:
              orgSelected &&
              "NORMAL" === orgSelected.organisationType &&
              features["maintenance_basic"] &&
              permissionsMap["MENU_ADMIN_MAINTENANCE"],
            isForceAvailableMenu: true,
            to: "/maintenance_programs",
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_MAINTENANCE_description_key,
            subPages: ["/maintenance_programs_manage"],
          },
          {
            view:
              features["dashboard"] && permissionsMap["MENU_ADMIN_DASHBOARD"],
            to: "/bi_dashboards",
            layout: "",
            name: language.UserPermission_MENU_ADMIN_DASHBOARD_description_key,
            subPages: ["/bi_dashboard_manage"],
          },
        ],
      },
      {
        collapse: true,
        name: language.access_management_key,
        state: "openAccess",
        icon: <span className="access-icon menu-icon"></span>,
        views: [
          {
            view: permissionsMap["MENU_ADMIN_USERS"],
            to: "/users_admin",
            layout: "",
            name: language.UserPermission_MENU_ADMIN_USERS_description_key,
            subPages: ["/users_admin_update"],
          },
          {
            view:
              features["user_profile"] &&
              permissionsMap["MENU_ADMIN_USER_ROLE"],
            to: "/role_manager_admin",
            layout: "",
            name: language.UserPermission_MENU_ADMIN_USER_ROLE_description_key,
            subPages: ["/role_manager_admin_update"],
          },
          {
            view:
              features["visibility_filters"] &&
              permissionsMap["MENU_ADMIN_VISIBILITY_FILTER"],
            to: "/manage_visibility_filters",
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_VISIBILITY_FILTER_description_key,
            subPages: ["/manage_visibility_filters_update"],
          },
          {
            view:
              orgSelected &&
              ("PARTNER" === loginInfo.organisation.organisationType ||
                "SYSTEM" === loginInfo.organisation.organisationType) &&
              "PARTNER" === orgSelected.organisationType &&
              features["organisation_visibility_filters"] &&
              permissionsMap["MENU_ADMIN_ORGANISATION_VISIBILITY_FILTER"],
            isForceAvailableMenu: true,
            to: "/manage_organisation_filters",
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_ORGANISATION_VISIBILITY_FILTER_description_key,
            subPages: ["/manage_organisation_filters_update"],
          },
          {
            view:
              features["int_sso"] &&
              permissionsMap["MENU_ADMIN_ORGANIZATION_SSO"] &&
              (organisation.organisationType !== "PARTNER" ||
                features["partner_org_manage"]),
            to: `/single_sign_on`,
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_ORGANIZATION_SSO_description_key,
          },
          {
            view:
              hasFeature(FeatureKey.audit_log) &&
              permissionsMap["MENU_ADMIN_AUDIT_LOG"],
            to: `/audit_log`,
            layout: "",
            name: language.UserPermission_MENU_ADMIN_AUDIT_LOG_description_key,
            subPages: ["/audit_log_view"],
          },
        ],
      },
      {
        collapse: true,
        name: language.organization_settings_key,
        state: "openOrgSetting",
        icon: <span className="org-admin-menu-icon menu-icon"></span>,
        views: [
          {
            view:
              permissionsMap["MENU_ADMIN_ORGANIZATION_DETAILS"] &&
              (organisation.organisationType !== "PARTNER" ||
                features["partner_org_manage"]),
            to: `/organization_update`,
            param: permissionsMap["SYSTEM_ADMIN_VIEW"]
              ? "type=edit"
              : "type=view",
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_ORGANIZATION_DETAILS_description_key,
          },
          {
            view:
              permissionsMap["MENU_ADMIN_PLATFORM_PLAN"] &&
              (organisation.organisationType !== "PARTNER" ||
                features["partner_org_manage"]),
            to: `/product_plan`,
            param: permissionsMap["SYSTEM_ADMIN_VIEW"]
              ? "type=edit"
              : "type=view",
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_PLATFORM_PLAN_description_key,
          },
          {
            view:
              loginInfo.organisation.organisationType === "SYSTEM" &&
              orgSelected &&
              "PARTNER" === orgSelected.organisationType &&
              permissionsMap["QUOTE_VIEW"],
            isForceAvailableMenu: true,
            to: `/quotes_partner`,
            layout: "",
            name: language.quotes_key,
            subPages: ["/quote_detail_partner"],
          },
          {
            view:
              orgSelected &&
              features["show_invoicing"] &&
              permissionsMap["MENU_ADMIN_BILLING"] &&
              "SYSTEM" !== orgSelected.organisationType,
            isForceAvailableMenu: true,
            to: `/invoicing`,
            layout: "",
            name: language.UserPermission_MENU_ADMIN_BILLING_description_key,
            subPages: ["/invoice_detail", "/quotation_detail"],
          },
          {
            view:
              orgSelected &&
              permissionsMap["MENU_ADMIN_PARTNER_DETAIL"] &&
              "SYSTEM" !== orgSelected.organisationType,
            isForceAvailableMenu: true,
            to: "/partners_details",
            param: "type=view&id=&tabActive=general_tab",
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_PARTNER_DETAIL_description_key,
          },
        ],
      },
      {
        collapse: true,
        name: language.customization_key,
        state: "opencustomization",
        icon: <span className="ui-custom-icon menu-icon"></span>,
        views: [
          {
            view:
              features["landing_page"] &&
              permissionsMap["MENU_ADMIN_LANDING_PAGE"] &&
              (organisation.organisationType !== "PARTNER" ||
                features["partner_org_manage"]),
            to: `/default_landing_page`,
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_LANDING_PAGE_description_key,
          },
          {
            view:
              features["report_generator"] &&
              permissionsMap["MENU_ADMIN_REPORT_TEMPLATE"],
            to: "/report_template_library",
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_REPORT_TEMPLATE_description_key,
            subPages: ["/report_template_library_manage"],
          },
          {
            view:
              features["image_library"] &&
              permissionsMap["MENU_ADMIN_IMAGE_LIBRARY"],
            to: "/image_library",
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_IMAGE_LIBRARY_description_key,
            subPages: ["/image_library_manage"],
          },
          {
            view:
              features["custom_i18n_message"] &&
              permissionsMap["MENU_ADMIN_CUSTOM_I18N_MESSAGE"],
            to: "/custom_i18n_messages",
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_CUSTOM_I18N_MESSAGE_description_key,
            subPages: ["/custom_i18n_messages_manage"],
          },
          {
            view:
              features["custom_interface"] &&
              permissionsMap["MENU_ADMIN_CUSTOM_INTERFACE"] &&
              (organisation.organisationType !== "PARTNER" ||
                features["partner_org_manage"]),
            to: `/custom_branding`,
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_CUSTOM_INTERFACE_description_key,
          },
          {
            view: permissionsMap["MENU_ADMIN_SUPPORT"],
            to: `/support`,
            layout: "",
            name: language.UserPermission_MENU_ADMIN_SUPPORT_description_key,
          },
        ],
      },
      {
        collapse: true,
        name: language.developers_key,
        state: "openUtilization",
        icon: <span className="dev-admin-menu-icon menu-icon"></span>,
        views: [
          {
            view: permissionsMap["MENU_ADMIN_API_DOCS"],
            to: "/api-docs",
            layout: "",
            name: language.UserPermission_MENU_ADMIN_API_DOCS_description_key,
          },
          {
            view: permissionsMap["MENU_ADMIN_API_ACCESS"],
            to: "/api-access",
            layout: "",
            name: language.UserPermission_MENU_ADMIN_API_ACCESS_description_key,
            subPages: ["/api-access-update"],
          },
          {
            view:
              features["business_inject"] &&
              permissionsMap["MENU_ADMIN_BUSINESS_CHANNEL"] &&
              (organisation.organisationType !== "PARTNER" ||
                features["partner_org_manage"]),
            to: `/third_party_data`,
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_BUSINESS_CHANNEL_description_key,
            subPages: ["/channel_update"],
          },
          {
            view:
              features["int_rest_mqtt_push"] &&
              permissionsMap["MENU_ADMIN_WEBHOOKS"] &&
              (organisation.organisationType !== "PARTNER" ||
                features["partner_org_manage"]),
            to: `/webhooks`,
            layout: "",
            name: language.UserPermission_MENU_ADMIN_WEBHOOKS_description_key,
            subPages: ["/callbacks_update"],
          },
          {
            view:
              features["int_api_clientapp"] &&
              permissionsMap["MENU_ADMIN_CLIENT_APP_API"],
            to: "/client-app-api",
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_CLIENT_APP_API_description_key,
          },
        ],
      },
      {
        collapse: true,
        name: language.sidebar_support_key,
        state: "openInsights",
        icon: <span className="support-icon menu-icon"></span>,
        views: [
          {
            view: permissionsMap["MENU_ADMIN_RMA_DECLARATION"],
            to: `/rma_declaration_view`,
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_RMA_DECLARATION_description_key,
            subPages: ["/rma_declaration_view_details"],
          },
          {
            view:
              features["zendesk_ticket"] &&
              permissionsMap["MENU_ADMIN_ZENDESK_SUPPORT_TICKET"],
            to: "/support_tickets",
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_ZENDESK_SUPPORT_TICKET_description_key,
          },
          {
            view:
              (features["zendesk_ticket"] &&
                permissionsMap["SYSTEM_ADMIN_MANAGE"]) ||
              permissionsMap["SYSTEM_ADMIN_VIEW"],
            to: "/zendesk_tickets",
            layout: "",
            name: language.zendesk_ticket_key,
          },
          {
            view:
              features["zendesk_ticket"] &&
              permissionsMap["MENU_ADMIN_ZENDESK_ORDER"],
            to: "/order_tickets",
            layout: "",
            name:
              language.UserPermission_MENU_ADMIN_ZENDESK_ORDER_description_key,
          },
          {
            view: permissionsMap["MENU_ADMIN_CSM"],
            to: "/csm_info",
            layout: "",
            name: language.csm_info_menu_key,
          },
          {
            view: permissionsMap["MENU_ADMIN_CSM"],
            to: "/csm_comment",
            layout: "",
            name: language.csm_comment_menu_key,
            subPages: ["/csm_comment_create", "/csm_comment_update"],
          },
        ],
      },
    ];
  }, [loginInfo, language, orgSelected, organisation]);

  const [data, setData] = useState({
    type: MenuType.ADMIN,
    loading: true,
    routes: [],
    availableMenuPaths: {},
    allPaths: {},
    defaultPath: "/trackers_admin",
  });

  const availibleMenuData = useAvailibleMenu(routes, "/trackers_admin");

  useEffect(() => {
    setData({ ...data, ...availibleMenuData, loading: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availibleMenuData]);

  useEffect(() => {
    setSideBarColors(custumInfo);
  }, [custumInfo]);

  useEffect(() => {
    setData({ ...data, loading: true });
    if (orgId) {
      if (loginInfo.organisation.id !== orgId) {
        organizationClient.findOne({ id: orgId }).then(({ data }) => {
          setOrgSelected(data);
        });
      } else {
        setOrgSelected(loginInfo.organisation);
      }
    } else {
      setOrgSelected(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  return data;
};

export default useAdminMenu;
